export const isFalseyButNotZero = (n: any) => !n && n !== 0;

/**
 * Split an array into two buckets based on a predicate.
 *
 * @param {T[]} items
 * @param {(item: T) => boolean} predicate
 * @return {[T[], T[]]} tuple of [matching, non-matching]
 */
export function partition<T>(items: T[], predicate: (item: T) => boolean): [T[], T[]] {
	const yes: T[] = [];
	const no: T[] = [];
	for (const item of items) {
		if (predicate(item)) {
			yes.push(item);
		}
		else {
			no.push(item);
		}
	}
	return [yes, no];
}

/**
 * Check if a section slug indicates a replenish section
 *
 * @param {string} sectionSlug - The section slug to check
 * @return {boolean}
 */
export function isReplenishSection(sectionSlug: string): boolean {
	return sectionSlug.includes('event-repl');
}

/**
 * Check if a section slug indicates an extension section
 *
 * @param {string} sectionSlug - The section slug to check
 * @return {boolean}
 */
export function isExtensionSection(sectionSlug: string): boolean {
	return sectionSlug.includes('event-extension');
}

/**
 * Check if a section slug indicates either replenish or extension section
 *
 * @param {string} sectionSlug - The section slug to check
 * @return {boolean}
 */
export function isReplenishOrExtensionSection(sectionSlug: string): boolean {
	return isReplenishSection(sectionSlug) || isExtensionSection(sectionSlug);
}

/**
 * Check if a section slug indicates any type of event section
 *
 * @param {string} sectionSlug - The section slug to check
 * @return {boolean}
 */
export function isEventSection(sectionSlug: string): boolean {
	return sectionSlug.includes('event');
}

/**
 * Add a blink animation to an element by toggling a CSS class
 *
 * @param {HTMLElement} element
 * @param {string} className
 * @param {number} count
 * @return {void}
 */
export function addBlinkAnimation(element: HTMLElement | null, className: string, count: number): void {
	if (!element || count <= 0) {
		return;
	}

	element.classList.add(className);
	setTimeout(() => {
		element.classList.remove(className);
		setTimeout(() => {
			addBlinkAnimation(element, className, count - 1);
		}, 400);
	}, 400);
}

/**
 * Copy text to clipboard with optional success/error callbacks.
 *
 * @param {string} text - The text to copy to the clipboard
 * @param {Function} [onSuccess] - Optional callback invoked after a successful copy
 * @param {Function} [onError] - Optional callback invoked if the copy fails
 * @return {Promise<void>}
 */
export async function copyToClipboard(text: string, onSuccess?: () => void, onError?: (error: unknown) => void): Promise<void> {
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
		}
		// Fallback for browsers that don't support the Clipboard API
		else {
			const textArea = document.createElement('textarea');
			textArea.value = text;
			textArea.style.position = 'fixed';
			textArea.style.left = '-999999px';
			textArea.style.top = '-999999px';
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			document.execCommand('copy');
			document.body.removeChild(textArea);
		}

		onSuccess?.();
	}
	catch (error) {
		if (onError) {
			onError(error);
		}
		else {
			console.error('Failed to copy to clipboard:', error);
		}
	}
}

/**
 * Check if an integration type is a Pizzabot machine, whichever API version it is on.
 *
 * @param {string} [integrationType]
 * @return {boolean}
 */
export function isPizzabotIntegration(integrationType?: string): boolean {
	const type = integrationType?.toLowerCase();
	return type === 'pizzabot' || type === 'pizzabot-v2';
}

/**
 * Check if an integration type is the Pizzabot V2 API. V2 is the only version that splits a cart
 * into one child order per pizza, V1 keeps the cart as a single order with no parent/child.
 *
 * @param {string} [integrationType]
 * @return {boolean}
 */
export function isPizzabotV2Integration(integrationType?: string): boolean {
	return integrationType?.toLowerCase() === 'pizzabot-v2';
}
